import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Turnstile from "@/components/Turnstile";
import AuthShell from "@/components/AuthShell";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { motion } from "framer-motion";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [done, setDone] = useState("");
  const [resending, setResending] = useState(false);

  const handleToken = useCallback((t) => setToken(t), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await register(form.name, form.email, form.password, form.phone, token);
      if (data?.needs_verification) {
        setDone(form.email.trim().toLowerCase());
        return;
      }
      navigate("/account");
    } catch (e2) {
      setError(errMsg(e2));
      setToken("");
      setResetSignal((s) => s + 1);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: done, purpose: "account", origin_url: window.location.origin });
      toast.success("Verification email resent — check your inbox");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/account";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (done) {
    return (
      <div data-testid="register-check-email" className="max-w-lg mx-auto px-6 pt-40 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
          <img src="/logo.png" alt="Easy Drive Car Rental" className="h-24 w-auto object-contain mx-auto mb-8" />
          <MailCheck size={48} className="mx-auto text-primary" />
          <h1 className="mt-6 font-display text-5xl tracking-tight">Check your <span className="italic gold-text">inbox</span></h1>
          <p className="mt-5 text-lg text-muted-foreground font-ui leading-relaxed">
            We sent a verification link to <span className="text-foreground font-semibold">{done}</span>.
            Tap it to activate your account — then sign in and start earning loyalty discounts.
          </p>
          <p className="mt-8 text-base text-muted-foreground font-ui">
            Didn't get it?{" "}
            <button data-testid="register-resend-button" onClick={resend} disabled={resending} className="gold-text hover:underline font-ui">
              {resending ? "Sending…" : "Resend verification email"}
            </button>
          </p>
          <Link to="/login" className="inline-block mt-8 gold-text font-ui hover:underline">Back to sign in →</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <AuthShell
      testid="register-page"
      logoTestid="register-logo"
      eyebrow="Join Easy Drive Car Rental"
      title={<>Create your <span className="italic gold-text">account</span></>}
      sub="Book in under two minutes, track every rental in one place, and unlock loyalty discounts that grow with every trip."
      footer={
        <p className="mt-8 text-base text-muted-foreground font-ui">
          Already have an account? <Link data-testid="register-login-link" to="/login" className="gold-text hover:underline">Sign in</Link> — or book as a guest.
        </p>
      }
    >
      <form onSubmit={submit} className="mt-10 space-y-7">
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Full name</Label>
          <Input data-testid="register-name-input" required value={form.name} onChange={set("name")} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Email</Label>
          <Input data-testid="register-email-input" type="email" required value={form.email} onChange={set("email")} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Phone / WhatsApp (optional)</Label>
          <Input data-testid="register-phone-input" value={form.phone} onChange={set("phone")} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Password</Label>
          <Input data-testid="register-password-input" type="password" required minLength={6} value={form.password} onChange={set("password")} className="input-dark mt-2 h-12 text-base rounded-2xl px-4" />
          <p className="mt-2 text-xs text-muted-foreground font-ui">Minimum 6 characters. We'll email you a quick verification link after signup.</p>
        </div>
        <div>
          <Label className="font-ui text-sm tracking-wide text-muted-foreground">Security check</Label>
          <div className="mt-2">
            <Turnstile action="register" onToken={handleToken} resetSignal={resetSignal} />
          </div>
        </div>
        {error && <p data-testid="register-error" className="text-base text-destructive font-ui">{error}</p>}
        <Button data-testid="register-submit-button" disabled={loading || !token} className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12 text-base tracking-wide transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(199,30,46,0.35)]">
          {loading ? "Creating account…" : "Create Account"}
        </Button>
      </form>
      <div className="mt-8">
        <div className="flex items-center gap-4 text-sm text-muted-foreground font-ui">
          <div className="h-px flex-1 bg-black/10" />or<div className="h-px flex-1 bg-black/10" />
        </div>
        <button data-testid="google-signin-button" type="button" onClick={googleSignIn} className="mt-5 w-full rounded-full border border-border bg-black/5 hover:bg-black/10 transition-colors duration-200 h-12 flex items-center justify-center gap-3 text-base font-ui">
          <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
          </svg>
          Continue with Google
        </button>
      </div>
    </AuthShell>
  );
}
